import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, Filter, Search, Tag, X } from 'lucide-react';
import type { Asset } from '@khadi/shared';
import { api, apiBlob, downloadBlob, type ListResponse } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCategories, useLocations, useStatuses } from '../lib/queries';
import { formatDate } from '../lib/format';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatusPill, cn } from '../components/ui';
import { LabelSheet } from '../components/LabelSheet';

/**
 * Geräteliste — die meistbesuchte Seite.
 *
 * Auf schmalen Bildschirmen als Karten, ab Tablettbreite als Tabelle.
 * Archivierte Geräte sind ausgeblendet, aber einen Schalter entfernt
 * (in Snipe-IT waren sie nur über einen versteckten Filter erreichbar).
 */
export function AssetsPage() {
  const auth = useAuth();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statusId, setStatusId] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [locationId, setLocationId] = useState<number | ''>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [labelSheet, setLabelSheet] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const statuses = useStatuses();
  const categories = useCategories();
  const locations = useLocations();

  // In den Filtern nur zeigen, wonach sich überhaupt filtern lässt. Aus
  // Snipe-IT kamen acht Kategorien, von denen nur drei je ein Gerät gesehen
  // haben — leere Einträge machen die Auswahl nur unübersichtlich.
  const nurGenutzt = <T extends { assets_count?: number }>(rows: T[] | undefined) =>
    (rows ?? []).filter((row) => (row.assets_count ?? 0) > 0);

  const query = useMemo(
    () => ({
      q: debounced || undefined,
      status_id: statusId || undefined,
      category_id: categoryId || undefined,
      location_id: locationId || undefined,
      include_archived: includeArchived,
      limit: 200,
    }),
    [debounced, statusId, categoryId, locationId, includeArchived],
  );

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['assets', query],
    queryFn: () => api<ListResponse<Asset>>('/assets', { query }),
    placeholderData: keepPreviousData,
  });

  const activeFilters = [statusId, categoryId, locationId].filter(Boolean).length;

  async function exportList(): Promise<void> {
    const { blob, filename } = await apiBlob('/export/assets.xlsx', { query });
    downloadBlob(blob, filename);
  }

  function toggleSelection(id: number): void {
    setSelected((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Bestand"
        title="Geräte"
        subtitle={
          data ? `${data.total} ${data.total === 1 ? 'Gerät' : 'Geräte'} gefunden` : undefined
        }
        actions={
          <>
            {auth.can('export.read') && (
              <button type="button" onClick={() => void exportList()} className="btn btn-ghost btn-md">
                <Download className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
            {auth.can('asset.write') && (
              <Link to="/erfassen" className="btn btn-accent btn-md no-underline">
                Gerät erfassen
              </Link>
            )}
          </>
        }
      />

      {/* ---------- Suche und Filter ---------- */}
      <div className="mb-5 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--color-fg3)]"
              aria-hidden
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nummer, Seriennummer, Modell oder Person"
              className="field pl-9"
              aria-label="Geräte durchsuchen"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-[var(--color-fg3)] hover:text-[var(--color-fg)]"
                aria-label="Suche leeren"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((value) => !value)}
            className={cn('btn btn-ghost btn-md', showFilters && 'border-[var(--color-accent)]')}
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Filter</span>
            {activeFilters > 0 && (
              <span className="tabular grid h-5 min-w-5 place-items-center rounded-full bg-[var(--color-accent)] px-1 text-sm text-white">
                {activeFilters}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="surface animate-fade-up grid gap-3 p-4 sm:grid-cols-3">
            <div>
              <label className="label" htmlFor="filter-status">
                Status
              </label>
              <select
                id="filter-status"
                className="field"
                value={statusId}
                onChange={(event) => setStatusId(event.target.value ? Number(event.target.value) : '')}
              >
                <option value="">Alle</option>
                {nurGenutzt(statuses.data?.rows).map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name} ({status.assets_count})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="filter-category">
                Kategorie
              </label>
              <select
                id="filter-category"
                className="field"
                value={categoryId}
                onChange={(event) =>
                  setCategoryId(event.target.value ? Number(event.target.value) : '')
                }
              >
                <option value="">Alle</option>
                {nurGenutzt(categories.data?.rows).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.assets_count})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="filter-location">
                Standort
              </label>
              <select
                id="filter-location"
                className="field"
                value={locationId}
                onChange={(event) =>
                  setLocationId(event.target.value ? Number(event.target.value) : '')
                }
              >
                <option value="">Alle</option>
                {nurGenutzt(locations.data?.rows).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} ({location.assets_count})
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm sm:col-span-3">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(event) => setIncludeArchived(event.target.checked)}
                className="h-4 w-4 accent-[var(--color-accent)]"
              />
              Ausgemusterte Geräte mitzeigen
            </label>
          </div>
        )}
      </div>

      {/* ---------- Auswahlleiste für den Etikettendruck ---------- */}
      {selected.length > 0 && auth.can('label.print') && (
        <div className="surface animate-fade-up sticky top-16 z-20 mb-4 flex flex-wrap items-center justify-between gap-3 border-[var(--color-khadi-forest-400)] px-5 py-4">
          <p className="text-sm">
            <strong className="tabular">{selected.length}</strong>{' '}
            {selected.length === 1 ? 'Gerät' : 'Geräte'} ausgewählt
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setSelected([])} className="btn btn-quiet btn-sm">
              Auswahl aufheben
            </button>
            <button type="button" onClick={() => setLabelSheet(true)} className="btn btn-primary btn-sm">
              <Tag className="h-3.5 w-3.5" aria-hidden />
              Etiketten drucken
            </button>
          </div>
        </div>
      )}

      {isLoading && <Spinner label="Geräte werden geladen …" />}
      {error && <ErrorNote error={error} />}

      {data && data.rows.length === 0 && (
        <EmptyState
          title="Keine Geräte gefunden"
          description="Andere Suchbegriffe oder Filter probieren — oder ein neues Gerät erfassen."
        />
      )}

      {data && data.rows.length > 0 && (
        <div className={cn('transition-opacity', isFetching && 'opacity-60')}>
          {/* Karten auf schmalen Bildschirmen */}
          <ul className="stagger space-y-3 lg:hidden">
            {data.rows.map((asset) => (
              <li key={asset.id}>
                <Link
                  to="/geraete/$assetId"
                  params={{ assetId: String(asset.id) }}
                  className="surface card-link block px-5 py-4 no-underline"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="tabular text-sm font-semibold text-[var(--color-fg)]">
                        {asset.asset_tag}
                      </p>
                      <p className="truncate text-[0.9375rem] text-[var(--color-fg1)]">
                        {asset.model?.name ?? 'Ohne Modell'}
                      </p>
                      <p className="mt-0.5 truncate text-sm text-[var(--color-fg3)]">
                        {asset.holder?.name ?? 'Nicht zugewiesen'} · {asset.serial}
                      </p>
                    </div>
                    <StatusPill name={asset.status.name} archived={asset.status.is_archived} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {/* Tabelle ab großer Breite */}
          <div className="surface hidden overflow-hidden lg:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-line)] bg-[var(--color-bg-tinted)]/60 text-left">
                  <th className="w-10 px-4 py-3.5" />
                  <th className="px-4 py-3.5 font-semibold">Nummer</th>
                  <th className="px-4 py-3.5 font-semibold">Modell</th>
                  <th className="px-4 py-3.5 font-semibold">Seriennummer</th>
                  <th className="px-4 py-3.5 font-semibold">Zugewiesen an</th>
                  <th className="px-4 py-3.5 font-semibold">Ort</th>
                  <th className="px-4 py-3.5 font-semibold">Status</th>
                  <th className="px-4 py-3.5 font-semibold">Kaufdatum</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((asset) => (
                  <tr key={asset.id} className="row-link border-b border-[var(--color-line)] last:border-0">
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selected.includes(asset.id)}
                        onChange={() => toggleSelection(asset.id)}
                        className="h-4 w-4 accent-[var(--color-accent)]"
                        aria-label={`${asset.asset_tag} auswählen`}
                      />
                    </td>
                    <td className="tabular px-5 py-4 font-semibold">
                      <Link
                        to="/geraete/$assetId"
                        params={{ assetId: String(asset.id) }}
                        className="no-underline hover:text-[var(--color-accent)]"
                      >
                        {asset.asset_tag}
                      </Link>
                    </td>
                    <td className="max-w-xs truncate px-5 py-4">{asset.model?.name ?? '—'}</td>
                    <td className="tabular px-5 py-4 text-[var(--color-fg2)]">{asset.serial}</td>
                    <td className="max-w-[14rem] truncate px-5 py-4">
                      {asset.holder?.name ?? (
                        <span className="text-[var(--color-fg3)]">nicht zugewiesen</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {/* Nur eine wirklich fehlende Angabe wird gedämpft —
                          „beim Mitarbeiter" ist eine vollwertige Auskunft. */}
                      <span
                        className={cn(
                          asset.current_location?.source === 'unknown' &&
                            'text-[var(--color-fg3)] italic',
                        )}
                        title={
                          asset.current_location?.source === 'with_employee'
                            ? `Ortsungebunden bei ${asset.current_location.holder_name ?? 'der Person'}`
                            : asset.current_location?.source === 'employee_location'
                              ? `Standort von ${asset.current_location.holder_name ?? 'der Person'}`
                              : asset.current_location?.source === 'home_location'
                                ? 'Hinterlegter Standort des Geräts'
                                : undefined
                        }
                      >
                        {asset.current_location?.name ?? '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <StatusPill name={asset.status.name} archived={asset.status.is_archived} />
                    </td>
                    <td className="tabular px-5 py-4 text-[var(--color-fg3)]">
                      {formatDate(asset.purchase_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {labelSheet && (
        <LabelSheet assetIds={selected} onClose={() => setLabelSheet(false)} />
      )}
    </div>
  );
}
